import classNames from 'clsx';
import hotkeys from 'hotkeys-js';
import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IS_B2B_BIZ_USER, NEW_PORTAL_HEADER } from 'reactApp/appHelpers/configHelpers';
import { IS_DUPLICATES_ENABLED } from 'reactApp/appHelpers/featuresHelpers/features/duplicates';
import { useHotKeyScope } from 'reactApp/hooks/useHotkeyScope';
import { createAlbumRequest, loadAlbumsListRequest } from 'reactApp/modules/albums/albums.actions';
import { CUSTOM_ALBUMS_ID } from 'reactApp/modules/albums/albums.constants';
import { getCustomAlbumsErrorState, getCustomAlbumsList, getCustomAlbumsLoadingState } from 'reactApp/modules/albums/albums.selector';
import { loadDuplicatesCountRequest } from 'reactApp/modules/duplicates/duplicates.actions';
import {
    getDuplicatesGroupsCount,
    getDuplicatesGroupsCountErrorState,
    getDuplicatesGroupsCountLoadingState,
    getDuplicatesPreviewItemsErrorState,
    getDuplicatesPreviewItemsLoadingState,
} from 'reactApp/modules/duplicates/duplicates.selectors';
import { routeChangeSuccess } from 'reactApp/modules/router/router.module';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { disableDropArea, enableDropArea } from 'reactApp/modules/uploading/uploading.module';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { UserQuotaSelectors } from 'reactApp/modules/userQuota/userQuota.selectors';
import { Album } from 'reactApp/ui/Album/Album';
import { BreadcrumbsContainer } from 'reactApp/ui/BreadcrumbsContainer/BreadcrumbsContainer';
import { CompilationsSection } from 'reactApp/ui/Compilations/CompilationsSection/CompilationsSection';
import { CreateAlbum } from 'reactApp/ui/CreateAlbum/CreateAlbum';
import { DuplicatesPreview } from 'reactApp/ui/Duplicates/DuplicatesPreview/DuplicatesPreview';
import { Empty } from 'reactApp/ui/Empty/Empty';
import { ErrorRetry } from 'reactApp/ui/ErrorRetry/ErrorRetry';
import { Footer } from 'reactApp/ui/Footer/Footer';
import { Spinner } from 'reactApp/ui/Spinner/Spinner';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';

import { AlbumsEmpty } from './AlbumsEmpty/AlbumsEmpty';
import styles from './AlbumsPage.css';
import { AlbumsSection } from './AlbumsSection/AlbumsSection';

const HOT_KEY_SCOPE_NAME = 'albums_page';

// eslint-disable-next-line max-lines-per-function
export const AlbumsPage = memo(function AlbumsPage() {
    const dispatch = useDispatch();
    const customAlbums = useSelector(getCustomAlbumsList);
    const isEmptyAlbumsList = !customAlbums.length;

    const isAlbumsLoading = useSelector(getCustomAlbumsLoadingState);
    const isDuplicatesPreviewLoading = useSelector(getDuplicatesPreviewItemsLoadingState);
    const isDuplicatesGroupCountLoading = useSelector(getDuplicatesGroupsCountLoadingState);

    const isOverquota = useSelector(UserQuotaSelectors.isOverquota);
    const isQuotaLoaded = useSelector(UserQuotaSelectors.isQuotaLoaded);
    const isBizUser = useSelector(UserSelectors.isBizUser);
    const isB2BUser = useSelector(UserSelectors.isB2BUser);

    const isDuplicatesEnabled = IS_DUPLICATES_ENABLED && !isOverquota && isQuotaLoaded && !isBizUser && !isB2BUser;

    const getIsLoading = () => {
        if (isDuplicatesEnabled) {
            return isAlbumsLoading || isDuplicatesPreviewLoading || isDuplicatesGroupCountLoading;
        }

        return isAlbumsLoading;
    };

    const isAlbumError = useSelector(getCustomAlbumsErrorState);
    const isDuplicatesPreviewError = useSelector(getDuplicatesPreviewItemsErrorState);
    const isDuplicatesGroupCountError = useSelector(getDuplicatesGroupsCountErrorState);
    const getIsError = () => {
        if (isDuplicatesEnabled) {
            return isAlbumError && isDuplicatesPreviewError && isDuplicatesGroupCountError;
        }

        return isAlbumError;
    };

    const duplicatesGroupsCount = useSelector(getDuplicatesGroupsCount);
    const showDuplicatesSection = isDuplicatesEnabled && duplicatesGroupsCount > 0;

    const createAlbumHandler = useCallback(() => {
        dispatch(createAlbumRequest());
    }, []);

    useHotKeyScope(HOT_KEY_SCOPE_NAME);

    useEffect(() => {
        const handler = function (e) {
            e.preventDefault();

            createAlbumHandler();
        };

        hotkeys(`shift+a`, HOT_KEY_SCOPE_NAME, handler);

        return () => {
            hotkeys.unbind(`shift+a`, HOT_KEY_SCOPE_NAME, handler);
        };
    }, [createAlbumHandler]);

    useEffect(() => {
        dispatch(routeChangeSuccess({ id: '/', storage: EStorageType.albums, params: {}, __isFolder: true, url: '', __parent: '' }));
        dispatch(loadAlbumsListRequest());
        if (isDuplicatesEnabled) {
            dispatch(loadDuplicatesCountRequest());
        }
    }, [dispatch, isDuplicatesEnabled]);

    useEffect(() => {
        sendPaymentGa({ eventCategory: ECategoryGa.entered, action: 'albums' });
        sendPaymentGa({ eventCategory: ECategoryGa.entered, action: 'page-entered', source: 'albums' });
    }, []);

    useEffect(() => {
        dispatch(disableDropArea());

        return () => {
            dispatch(enableDropArea());
        };
    }, []);

    const content = useMemo(() => {
        if (getIsError()) {
            return <ErrorRetry centered />;
        }

        if (getIsLoading()) {
            return <Spinner />;
        }

        if (isEmptyAlbumsList) {
            if (showDuplicatesSection) {
                return (
                    <div>
                        <AlbumsSection title="Мои альбомы" sectionRouteId={CUSTOM_ALBUMS_ID}>
                            <div className={styles.album}>
                                <CreateAlbum onClick={createAlbumHandler} />
                            </div>
                        </AlbumsSection>
                        <CompilationsSection>
                            <div data-qa-compilation="duplicates">
                                <DuplicatesPreview />
                            </div>
                        </CompilationsSection>
                    </div>
                );
            }

            return (
                <div className={styles.emptyContent} data-qa-id="albums-empty">
                    {/* TODO: CLOUDWEB-14876 - заменить на новый компонент empty-state'а после аб. */}
                    {IS_B2B_BIZ_USER ? (
                        <Empty.New storage={EStorageType.albums} categoryType={EStorageType.albums} fullHeight />
                    ) : (
                        <AlbumsEmpty />
                    )}
                </div>
            );
        }

        return (
            <div>
                <AlbumsSection title="Мои альбомы" sectionRouteId={CUSTOM_ALBUMS_ID}>
                    <div className={styles.album}>
                        <CreateAlbum onClick={createAlbumHandler} />
                    </div>
                    {customAlbums.map((album) => (
                        <Album key={album.id} {...album} />
                    ))}
                </AlbumsSection>
                {showDuplicatesSection && (
                    <CompilationsSection>
                        <div data-qa-compilation="duplicates">
                            <DuplicatesPreview />
                        </div>
                    </CompilationsSection>
                )}
            </div>
        );
    }, [getIsError, isEmptyAlbumsList, getIsLoading, customAlbums, showDuplicatesSection]);

    return (
        <div
            className={classNames(styles.root, {
                [styles.root_new_portal_header]: NEW_PORTAL_HEADER,
            })}
        >
            <div className={styles.wrapper}>
                <div className={styles.header}>
                    <BreadcrumbsContainer />
                </div>

                {content}

                <Footer storage={EStorageType.albums} isAbsolutePos={true} />
            </div>
        </div>
    );
});

export default AlbumsPage;
